import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Home, Calendar, User, LogOut, Menu, X, Users, Shield, UserCog, Coins, Target, Compass, Bell, Heart, MessageCircle, UserPlus, Trophy, Check, MapPin, Loader2, Swords, Star, Gamepad2, Search, Camera, Settings } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { LightModeIcon, DarkModeIcon } from "@/components/ThemeIcons";

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setLightTheme, setDarkTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Search states
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef(null);

  // Dark mode flag
  const isDark = theme === 'dark';

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user || user.role === "venue_owner") return;
      try {
        const response = await axios.get(`${API}/notifications`);
        const notifs = response.data || [];
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    
    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Search users
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearchLoading(true);
      try {
        const response = await axios.get(`${API}/users/search?q=${searchQuery}&limit=10`);
        setSearchResults(response.data?.users || response.data || []);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearchSelect = (selectedUser) => {
    navigate(`/profile/${selectedUser.id}`);
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Mark all as read
      const unreadNotifs = notifications.filter(n => !n.read);
      await Promise.all(unreadNotifs.map(n => 
        axios.put(`${API}/notifications/${n.id}/read`)
      ));
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Hikaye bildirimleri için özel işlem
    if ((notification.type === 'story_mention' || notification.type === 'story_reaction') && notification.data?.fromUserId) {
      // Hikayeyi paylaşan kullanıcının profiline yönlendir
      navigate(`/profile/${notification.data.fromUserId}`);
      setNotificationsOpen(false);
      return;
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    setNotificationsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
      case 'post_liked':
        return <Heart className="h-4 w-4 text-red-500" />;
      case 'comment':
      case 'post_comment':
        return <MessageCircle className="h-4 w-4 text-blue-500" />;
      case 'connection_request':
      case 'connection_accepted':
        return <UserPlus className="h-4 w-4 text-emerald-500" />;
      case 'match_invite':
      case 'match_update':
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 'club_invite':
      case 'application_accepted':
        return <Shield className="h-4 w-4 text-purple-500" />;
      case 'check_in':
        return <MapPin className="h-4 w-4 text-blue-500" />;
      case 'story_mention':
        return <Camera className="h-4 w-4 text-pink-500" />;
      case 'story_reaction':
        return <Heart className="h-4 w-4 text-pink-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return "Az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} gün`;
    return date.toLocaleDateString('tr-TR');
  };

  // Navigation items based on user role
  const playerNavItems = [
    { label: "Ana Sayfa", path: "/", icon: Home },
    { label: "Keşfet", path: "/social", icon: Compass },
    { label: "Sahalar", path: "/venues", icon: Calendar },
  ];
  
  // Dropdown items (Daha Fazla menüsü)
  const playerDropdownItems = [
    { label: "Açık Pozisyonlar", path: "/open-positions", icon: Users },
    { label: "Rakip Bul", path: "/rival-ads", icon: Target },
    { label: "Kulüpler", path: "/clubs", icon: Shield },
    { label: "Turnuvalar", path: "/tournaments", icon: Trophy },
    { label: "Mini Oyun", path: "/headball", icon: Gamepad2 },
    { label: "Mesajlar", path: "/messages", icon: MessageCircle },
    { label: "Sıralama", path: "/rankings", icon: Star },
    { label: "Kredi", path: "/credits", icon: Coins },
  ];

  const venueOwnerNavItems = [
    { label: "Ana Sayfa", path: "/venue-owner-dashboard", icon: Home },
    { label: "Rezervasyon Takvimi", path: "/reservation-calendar", icon: Calendar },
    { label: "Fiyatlandırma", path: "/dynamic-pricing", icon: Calendar },
    { label: "Gelir Analizi", path: "/revenue-analytics", icon: Calendar },
  ];

  const adminNavItems = [
    { label: "Kullanıcılar", path: "/admin?tab=users", icon: Users },
    { label: "Tüm Maçlar", path: "/admin?tab=matches", icon: Calendar },
    { label: "Tüm Rezervasyonlar", path: "/admin?tab=bookings", icon: Calendar },
    { label: "Tüm Sahalar", path: "/admin?tab=venues", icon: Home },
  ];

  // Choose nav items based on user role
  const isAdmin = user?.isAdmin === true;
  const navItems = isAdmin ? adminNavItems : (user?.role === "venue_owner" ? venueOwnerNavItems : playerNavItems);
  const dropdownItems = (!isAdmin && user?.role !== "venue_owner") ? playerDropdownItems : [];

  const isActive = (path) => location.pathname === path;
  const isDropdownActive = dropdownItems.some(item => location.pathname === item.path);

  return (
    <nav className={`border-b sticky top-0 z-50 shadow-sm transition-colors duration-300 ${isDark ? 'bg-[#1f2224] border-[#3a3d40]' : 'bg-white border-gray-200'}`} data-testid="navbar">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              if (isAdmin) {
                navigate("/admin");
              } else if (user?.role === "venue_owner") {
                navigate("/venue-owner-dashboard");
              } else {
                navigate("/");
              }
            }}
            data-testid="navbar-logo"
          >
            <span className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Krampon
            </span>
          </div>

          {/* Search Bar */}
          {!isAdmin && user?.role !== "venue_owner" && (
            <Popover open={searchOpen} onOpenChange={setSearchOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  className={`hidden sm:flex items-center gap-2 px-4 ${isDark ? 'text-gray-300 hover:text-white bg-[#2a2d2f] hover:bg-[#3a3d40]' : 'text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
                  data-testid="search-button"
                >
                  <Search className="h-4 w-4" />
                  <span className="text-sm">Kullanıcı ara...</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className={`p-3 border-b ${isDark ? 'border-[#3a3d40]' : ''}`}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      ref={searchInputRef}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="İsim veya email ile ara..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <ScrollArea className="max-h-80">
                  {searchLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((result) => (
                        <button
                          key={result.id}
                          onClick={() => handleSearchSelect(result)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 transition-colors"
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={result.profileImage} />
                            <AvatarFallback className="bg-emerald-100 text-emerald-700">
                              {result.name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-sm">{result.name}</p>
                            <p className="text-xs text-gray-500">{result.city || result.email}</p>
                          </div>
                          {result.position && (
                            <Badge variant="outline" className="text-xs">
                              {result.position === 'forward' ? 'Forvet' : 
                               result.position === 'midfielder' ? 'Orta Saha' :
                               result.position === 'defender' ? 'Defans' : 'Kaleci'}
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : searchQuery.length >= 2 ? (
                    <div className="py-6 text-center text-gray-500 text-sm">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Kullanıcı bulunamadı</p>
                    </div>
                  ) : (
                    <div className="py-6 text-center text-gray-400 text-sm">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Aramak için en az 2 karakter yazın</p>
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? "default" : "ghost"}
                  className={isActive(item.path) ? "" : ""}
                  style={isActive(item.path) ? { background: 'rgba(147, 112, 219, 0.6)' } : {}}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.path}`}
                  size="sm"
                >
                  <Icon className="h-4 w-4 mr-1.5" />
                  {item.label}
                </Button>
              );
            })}
            
            {/* Daha Fazla Dropdown */}
            {dropdownItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant={isDropdownActive ? "default" : "ghost"}
                    className={isDropdownActive ? "" : isDark ? 'text-white hover:bg-[#2a2d2f]' : ''}
                    style={isDropdownActive ? { background: 'rgba(147, 112, 219, 0.6)' } : {}}
                    size="sm"
                  >
                    <Menu className="h-4 w-4 mr-1.5" />
                    Daha Fazla
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-48`}>
                  {dropdownItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={isActive(item.path) ? (isDark ? 'text-purple-300' : 'text-purple-700') : ''}
                        style={isActive(item.path) ? { background: isDark ? 'rgba(147, 112, 219, 0.15)' : 'rgba(147, 112, 219, 0.1)' } : {}}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-2">
            {/* Notifications Bell - Only for players */}
            {user?.role !== "venue_owner" && !isAdmin && (
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    data-testid="notifications-btn"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 text-white text-xs rounded-full flex items-center justify-center font-bold" style={{ background: 'rgba(147, 112, 219, 0.85)' }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-80 p-0 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`} align="end">
                  <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-[#3a3d40]' : ''}`}>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : ''}`}>Bildirimler</h3>
                    {unreadCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs text-emerald-600 hover:text-emerald-700"
                        onClick={markAllAsRead}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Tümünü Okundu İşaretle
                      </Button>
                    )}
                  </div>
                  
                  <ScrollArea className="h-[300px]">
                    {notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                        <Bell className="h-10 w-10 mb-2 opacity-30" />
                        <p className="text-sm">Henüz bildirim yok</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {notifications.slice(0, 20).map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition ${
                              !notification.read ? 'bg-emerald-50/50' : ''
                            }`}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatTime(notification.createdAt)}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-2" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  
                  <div className="border-t p-2">
                    <Button 
                      variant="ghost" 
                      className="w-full text-sm text-emerald-600 hover:text-emerald-700"
                      onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/connections');
                      }}
                    >
                      Bağlantı İsteklerini Gör
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Theme Toggle - Bildirim ikonunun yanında */}
            <div className="flex items-center gap-1 px-2 bg-gray-100 dark:bg-dark-card rounded-full p-1">
              <button
                onClick={() => setLightTheme()}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                  theme === 'light' 
                    ? 'bg-gradient-to-br from-amber-200 to-amber-400 shadow-lg shadow-amber-200/50 scale-110 ring-2 ring-amber-300' 
                    : 'bg-gray-200 dark:bg-dark-accent opacity-60 hover:opacity-100 hover:scale-105'
                }`}
                title="Aydınlık Mod"
                data-testid="light-mode-btn"
              >
                <LightModeIcon className="w-7 h-7" />
              </button>
              <button
                onClick={() => setDarkTheme()}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-br from-dark-card to-dark-accent shadow-lg shadow-dark-card/50 scale-110 ring-2 ring-dark-accent' 
                    : 'bg-gray-200 dark:bg-dark-accent opacity-60 hover:opacity-100 hover:scale-105'
                }`}
                title="Karanlık Mod"
                data-testid="dark-mode-btn"
              >
                <DarkModeIcon className="w-7 h-7" />
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden dark:text-white dark:hover:bg-dark-card"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                  <Avatar>
                    <AvatarImage src={user?.profileImage} />
                    <AvatarFallback className="bg-emerald-600 text-white font-bold">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className={`flex items-center gap-2 p-2 ${isDark ? 'text-white' : ''}`}>
                  <Avatar>
                    <AvatarImage src={user?.profileImage} />
                    <AvatarFallback className="bg-emerald-600 text-white">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <p className="font-semibold text-sm">{user?.name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {/* Profile for Admin */}
                {user?.isAdmin ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/admin-profile")} data-testid="admin-profile-menu-item">
                      <User className="mr-2 h-4 w-4" />
                      Profilim
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    {/* Profile for Players */}
                    {user?.role !== "venue_owner" && (
                      <>
                        <DropdownMenuItem onClick={() => navigate(`/profile/${user.id}`)} data-testid="profile-menu-item">
                          <User className="mr-2 h-4 w-4" />
                          Profilim
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/connections')} data-testid="connections-menu-item">
                          <Users className="mr-2 h-4 w-4" />
                          Bağlantılar
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Profile for Venue Owners */}
                    {user?.role === "venue_owner" && (
                      <>
                        <DropdownMenuItem onClick={() => navigate("/venue-owner-profile")} data-testid="venue-owner-profile-menu-item">
                          <User className="mr-2 h-4 w-4" />
                          Profilim
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/venue-owner-dashboard")} data-testid="venue-owner-panel-menu-item">
                          <Shield className="mr-2 h-4 w-4" />
                          Ana Sayfa
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}
                
                {/* Role-based panel links */}
                {user?.role === "player" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/captain-dashboard")} data-testid="captain-panel-menu-item">
                      <Shield className="mr-2 h-4 w-4" />
                      Kaptan Paneli
                    </DropdownMenuItem>
                  </>
                )}
                
                {/* Admin Panel - Only for admin user */}
                {user?.isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-panel-menu-item">
                      <UserCog className="mr-2 h-4 w-4" />
                      Admin Paneli
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                  <Settings className="mr-2 h-4 w-4" />
                  Ayarlar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-1">
            {/* Ana menü öğeleri */}
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? "default" : "ghost"}
                  className={`w-full justify-start`}
                  style={isActive(item.path) ? { background: 'rgba(147, 112, 219, 0.6)' } : {}}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  size="sm"
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              );
            })}
            
            {/* Dropdown öğeleri de mobilde göster */}
            {dropdownItems.length > 0 && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                {dropdownItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Button
                      key={item.path}
                      variant={isActive(item.path) ? "default" : "ghost"}
                      className={`w-full justify-start`}
                  style={isActive(item.path) ? { background: 'rgba(147, 112, 219, 0.6)' } : {}}
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      size="sm"
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </Button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;